import { IconButton, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import ZDELine from './TimeCardLine'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { useTranslation } from 'react-i18next'
import { ITimecard, TimeCardLine } from '../../interface/ISelectedDate'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedDate } from '../../source/slice/SelectedDateSlice'
import { IUser } from '../../interface/IUser'
import { EmptyGuid, ENV_JID_BREAK, ENV_JID_START, ENV_JID_STOP } from '../../utils/values'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'

// Constants
const SPECIAL_JOB_IDS = ['RB', 'SE'] as const
const HOURS_TO_SECONDS = 3600
const PRECISION_THRESHOLD = 0.01

// Type definitions
type SpecialJobId = (typeof SPECIAL_JOB_IDS)[number]

const ZDELines = () => {
    const selectedDate: ITimecard = useSelector((state: any) => state.selectedDate?.value)
    const user: IUser = useSelector((state: any) => state.user?.value)
    const loadingDate: boolean = useSelector((state: any) => state.loadingDate?.value)

    const dispatch = useDispatch()
    const { t } = useTranslation()

    // Track last autofilled time to prevent infinite loops
    const lastAutoFilledTimeRef = useRef<number>(0)

    // Memoized selectors for better performance
    const timeEntries = useMemo(() => {
        if (!selectedDate?.lines) return { fromTime: undefined, toTime: undefined, breaks: [] }

        const fromTime = selectedDate.lines.find((line: TimeCardLine) => line.mserp_timeandattendancejobid === ENV_JID_START && !SPECIAL_JOB_IDS.includes(line.mserp_oncalltimeandattendancejobid as SpecialJobId))?.mserp_startdatetime

        const toTime = selectedDate.lines.find((line: TimeCardLine) => line.mserp_timeandattendancejobid === ENV_JID_STOP && !SPECIAL_JOB_IDS.includes(line.mserp_oncalltimeandattendancejobid as SpecialJobId))?.mserp_stopdatetime

        const breaks = selectedDate.lines.filter((line: TimeCardLine) => line.mserp_timeandattendancejobid === ENV_JID_BREAK && !line.delete)

        return { fromTime, toTime, breaks }
    }, [selectedDate?.lines])

    const linesWithSpecialOperation = useMemo(() => selectedDate?.lines?.filter((line: TimeCardLine) => line.mserp_oncalltimeandattendancejobid === 'SE') || [], [selectedDate?.lines])

    const hasSpecialOperation = linesWithSpecialOperation.length > 0
    const formDisabled = selectedDate?.header?.isTransferred || selectedDate?.header?.calculated || selectedDate?.dayInformation?.mserp_transferred

    // Pure helper functions
    const calculateBreaksTime = useCallback((breaks: TimeCardLine[]): number => {
        return breaks.reduce((total, breakItem) => {
            const breakTime = breakItem.mserp_calculatedtimeseconds ?? 0
            return total + breakTime / HOURS_TO_SECONDS
        }, 0)
    }, [])

    const calculateWorkingTime = useCallback((fromTime?: string, toTime?: string, breaksTime: number = 0): number => {
        if (!fromTime || !toTime) return 0

        const timeDiffHours = (new Date(toTime).getTime() - new Date(fromTime).getTime()) / (1000 * HOURS_TO_SECONDS)
        return Number((timeDiffHours - breaksTime).toFixed(2))
    }, [])

    const calculateProjectTimeSum = useCallback((lines: TimeCardLine[]): number => {
        return Number(
            lines
                .reduce((sum, line) => {
                    // Skip empty lines, breaks, special operations, and deleted lines
                    const isEmptyLine = !line.mserp_projectid && !line.mserp_timeandattendancejobid
                    const isSpecialLine = line.mserp_timeandattendancejobid === ENV_JID_BREAK || line.mserp_oncalltimeandattendancejobid === 'SE' || line.delete

                    if (!line.mserp_calculatedtimeseconds || isEmptyLine || isSpecialLine) {
                        return sum
                    }

                    return sum + line.mserp_calculatedtimeseconds / HOURS_TO_SECONDS
                }, 0)
                .toFixed(2)
        )
    }, [])

    const isEmptyProjectLine = useCallback((line: TimeCardLine): boolean => {
        return (
            !line.mserp_projectid &&
            !line.mserp_timeandattendancejobid &&
            line.mserp_timeandattendancejobid !== ENV_JID_BREAK &&
            line.mserp_timeandattendancejobid !== ENV_JID_START &&
            line.mserp_timeandattendancejobid !== ENV_JID_STOP &&
            !line.delete
        )
    }, [])

    // Memoized calculations
    const breaksTime = useMemo(() => calculateBreaksTime(timeEntries.breaks), [calculateBreaksTime, timeEntries.breaks])

    const workingTime = useMemo(() => calculateWorkingTime(timeEntries.fromTime, timeEntries.toTime, breaksTime), [calculateWorkingTime, timeEntries.fromTime, timeEntries.toTime, breaksTime])

    const projectTimeSum = useMemo(() => (selectedDate?.lines ? calculateProjectTimeSum(selectedDate.lines) : 0), [calculateProjectTimeSum, selectedDate?.lines])

    const remainingTime = workingTime - projectTimeSum
    const workingTimeDisplay = isNaN(workingTime) ? 0 : workingTime
    const remainingTimeDisplay = isNaN(remainingTime) ? 0 : remainingTime

    // Autofill remaining time logic
    const updateAutoFilledTime = useCallback(
        (lines: TimeCardLine[], remainingTimeHours: number): TimeCardLine[] => {
            const linesCopy = [...lines]
            const emptyLineIndex = linesCopy.findIndex(isEmptyProjectLine)

            if (emptyLineIndex === -1) return linesCopy

            if (remainingTimeHours > 0) {
                // Fill the first empty line with remaining time
                const remainingTimeInSeconds = Math.round(remainingTimeHours * HOURS_TO_SECONDS)
                linesCopy[emptyLineIndex] = {
                    ...linesCopy[emptyLineIndex],
                    mserp_calculatedtimeseconds: remainingTimeInSeconds,
                }

                // Clear other empty lines
                linesCopy.forEach((line, index) => {
                    if (index !== emptyLineIndex && isEmptyProjectLine(line)) {
                        linesCopy[index] = {
                            ...linesCopy[index],
                            mserp_calculatedtimeseconds: 0,
                        }
                    }
                })
            } else {
                // Clear all autofilled empty lines when no remaining time
                linesCopy.forEach((line, index) => {
                    if (isEmptyProjectLine(line) && line.mserp_calculatedtimeseconds && line.mserp_calculatedtimeseconds > 0) {
                        linesCopy[index] = {
                            ...linesCopy[index],
                            mserp_calculatedtimeseconds: 0,
                        }
                    }
                })
            }

            return linesCopy
        },
        [isEmptyProjectLine]
    )

    // Autofill effect
    useEffect(() => {
        if (!selectedDate?.lines || loadingDate || formDisabled) return

        const roundedRemainingTime = Math.round(remainingTime * 100) / 100

        // Prevent unnecessary updates
        if (Math.abs(roundedRemainingTime - lastAutoFilledTimeRef.current) < PRECISION_THRESHOLD) return

        const updatedLines = updateAutoFilledTime(selectedDate.lines, roundedRemainingTime)

        // Only update if there are actual changes
        const hasChanges = updatedLines.some((line, index) => line.mserp_calculatedtimeseconds !== selectedDate.lines![index].mserp_calculatedtimeseconds)

        if (hasChanges) {
            lastAutoFilledTimeRef.current = roundedRemainingTime
            dispatch(
                setSelectedDate({
                    ...selectedDate,
                    lines: updatedLines,
                })
            )
        }
    }, [remainingTime, selectedDate?.lines?.length, loadingDate, formDisabled, selectedDate, dispatch, updateAutoFilledTime])

    // Automatically add a new empty line when remainingTime > 0 and no empty line exists
    useEffect(() => {
        if (!selectedDate?.lines || loadingDate || formDisabled) return

        const hasEmptyLine = selectedDate.lines.some(isEmptyProjectLine)
        if (!hasEmptyLine && remainingTime >= PRECISION_THRESHOLD) {
            handleAddNewLine()
        }
    }, [selectedDate?.lines, remainingTime, loadingDate, formDisabled])

    // Add new line handler
    const handleAddNewLine = useCallback(() => {
        if (!selectedDate) return

        const newEmptyLine: Partial<TimeCardLine> = {
            mserp_timeprofiledate: selectedDate.header?.mserp_timeprofiledate,
            mserp_workerpersonnelnumber: selectedDate.header?.mserp_workerpersonnelnumber,
            mserp_dataareaid: user.resource.mserp_resourcecompanyid,
            mserp_mbelectronictimecardlineentityid: EmptyGuid,
            mserp_startdatetime: '',
            mserp_stopdatetime: '',
            mserp_linenumber: 0,
            mserp_projectid: '',
            mserp_projectcategoryid: '',
            mserp_timeandattendancejobid: '',
            mserp_indirectactivitycategoryid: '',
            mserp_calculatedtimeseconds: 0,
            mserp_purchid: '',
            mserp_purchlinenumber: 0,
            mserp_oncalltimeandattendancejobid: '',
            mserp_mboncallapplicationid: '',
            delete: false,
        }

        dispatch(
            setSelectedDate({
                ...selectedDate,
                lines: [...(selectedDate.lines || []), newEmptyLine as TimeCardLine],
            })
        )
    }, [selectedDate, user.resource.mserp_resourcecompanyid, dispatch])

    // Memoized table cells configuration
    const tableCells = useMemo(
        () => [
            { key: 'project', label: t('zdelines.project'), width: '100%' },
            { key: 'activity', label: t('zdelines.activity'), width: '100%' },
            ...(user?.worker?.mserp_employmentcategory === 'ANÜ' ? [{ key: 'order', label: t('zdelines.order'), width: '100%' }] : []),
            ...(hasSpecialOperation
                ? [
                      { key: 'amount', label: t('zdelines.amount'), width: { xs: 136, md: 152, lg: 136, xl: 152 } },
                      { key: 'from', label: t('zdelines.from'), width: { xs: 136, md: 152, lg: 136, xl: 152 } },
                      { key: 'to', label: t('zdelines.to'), width: { xs: 144, md: 152, lg: 144, xl: 152 } },
                  ]
                : [{ key: 'amount', label: t('zdelines.amount'), width: { xs: 144, md: 152, lg: 144, xl: 152 } }]),
        ],
        [t, user?.worker?.mserp_employmentcategory, hasSpecialOperation]
    )

    const handleKeyPress = (event: KeyboardEvent) => {
        if (formDisabled) return
        if (event.altKey) {
            if (event.key === '+') handleAddNewLine()
        }
    }

    useEffect(() => {
        document.addEventListener('keydown', handleKeyPress)
        return () => {
            document.removeEventListener('keydown', handleKeyPress)
        }
    }, [formDisabled, handleAddNewLine])

    return (
        <TableContainer component={Paper}>
            <Table
                sx={{
                    minWidth: hasSpecialOperation ? 900 : 700,
                    textOverflow: 'ellipsis',
                    tableLayout: 'fixed',
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell
                            sx={{
                                width: { xs: 84, md: 100, lg: 84, xl: 100 },
                                textAlign: 'center',
                                padding: { xs: '16px 8px', md: '16px', lg: '16px 8px', xl: '16px' },
                            }}
                        >
                            <Tooltip title={t('zdelines.add')}>
                                <span>
                                    <IconButton size="small" onClick={handleAddNewLine} disabled={formDisabled}>
                                        <AddCircleIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </TableCell>
                        {tableCells.map((cell) => (
                            <TableCell
                                key={cell.key}
                                sx={{
                                    width: cell.width,
                                    padding: { xs: '16px 8px', md: '16px', lg: '16px 8px', xl: '16px' },
                                }}
                            >
                                {cell.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {selectedDate?.lines?.map((row, index) => (
                        <ZDELine key={`${row.mserp_mbelectronictimecardlineentityid}-${index}`} row={row} id={index} hasSpecialOperation={hasSpecialOperation} />
                    ))}
                </TableBody>
            </Table>

            {!loadingDate ? (
                <Typography
                    sx={{
                        textAlign: 'end',
                        textWrap: 'balance',
                        color: remainingTimeDisplay !== 0 ? 'error.main' : 'success.main',
                        mb: 2,
                        mx: 2,
                        display: formDisabled ? 'none' : 'block',
                    }}
                >
                    {t('zdeheader.hoursdistribute', {
                        from: remainingTimeDisplay.toFixed(2),
                        to: workingTimeDisplay.toFixed(2),
                        formatParams: {
                            from: { maximumFractionDigits: 2, minimumFractionDigits: 2 },
                            to: { maximumFractionDigits: 2, minimumFractionDigits: 2 },
                        },
                    })}
                </Typography>
            ) : (
                <Skeleton
                    variant="text"
                    width="20%"
                    sx={{
                        float: 'right',
                        mb: 2,
                        ml: 2,
                        mr: 2,
                    }}
                />
            )}
        </TableContainer>
    )
}

export default ZDELines
